import { useState, useEffect } from 'react';
import CapstoneTaskCheckbox from './CapstoneTaskCheckbox';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Loader2, CheckCircle2 } from 'lucide-react';

export default function CapstoneTaskList({ capstoneId, onProgressChange }) {
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState({ total: 0, completed: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [capstoneId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/capstones/${capstoneId}/tasks`);
      setTasks(response.data.tasks);
      setProgress(response.data.progress);
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks');
      toast.error('Failed to load capstone tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskToggle = async (taskId, newCompletionStatus) => {
    // Refresh tasks to get updated progress
    await fetchTasks();
    // Notify parent component to refresh capstone data
    if (onProgressChange) {
      onProgressChange();
    }
  };

  const groupTasksByPhase = () => {
    const phases = ['setup', 'implementation', 'testing', 'deployment'];
    const grouped = {};

    phases.forEach(phase => {
      grouped[phase] = tasks.filter(task => task.phase === phase);
    });

    return grouped;
  };

  const getPhaseTitle = (phase) => {
    const titles = {
      setup: '🔧 Setup & Configuration',
      implementation: '💻 Implementation',
      testing: '🧪 Testing & Validation',
      deployment: '🚀 Deployment & Documentation',
    };
    return titles[phase] || phase;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-600">No tasks available for this capstone yet.</p>
      </div>
    );
  }

  const groupedTasks = groupTasksByPhase();

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Task Checklist</h3>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">
              {progress.completed} / {progress.total} tasks
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500 ease-out"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {progress.percentage.toFixed(0)}% complete
        </p>
      </div>

      {/* Tasks grouped by phase */}
      {Object.entries(groupedTasks).map(([phase, phaseTasks]) => {
        if (phaseTasks.length === 0) return null;

        const phaseCompleted = phaseTasks.filter(t => t.is_completed).length;
        const phaseTotal = phaseTasks.length;
        const phasePercentage = phaseTotal > 0 ? (phaseCompleted / phaseTotal) * 100 : 0;

        return (
          <div key={phase} className="space-y-3">
            {/* Phase Header */}
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">{getPhaseTitle(phase)}</h4>
              <span className="text-sm text-gray-600">
                {phaseCompleted}/{phaseTotal}
              </span>
            </div>

            {/* Phase Progress Bar */}
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 transition-all duration-300"
                style={{ width: `${phasePercentage}%` }}
              />
            </div>

            {/* Phase Tasks */}
            <div className="space-y-2">
              {phaseTasks.map(task => (
                <CapstoneTaskCheckbox
                  key={task.id}
                  task={task}
                  onToggle={handleTaskToggle}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
